import { IS_SEND_CODE, SESSION_USER } from "../../constants/login.constants";
import {
  IS_VERIFIED_AUTH,
  IS_VERIFIED_TYPE,
  REMEMBER_LOGIN,
  REMEMBER_VERIFY,
  USER_TOKEN_TYPE,
} from "constants/login.constants";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ProfileUser } from "shared/models/profile.model";
import { cookiesStorage } from "shared/helpers/cookiesStorage";
import { DAYS_EXPIRED_TOKEN } from "constants/common.constants";
import moment from "moment";
import dayjs from "dayjs";
import {
  ChargeBackStatus,
  ChargebackDateType,
  ChargebackSortBy,
} from "shared/models/chargeback.model";
import { DateType, KYCStatus } from "../../constants/onboarding.constants";

interface SessionUserProps {
  dashboard: {
    from_date: any;
    to_date: any;
    previous_from_date: any;
    previous_to_date: any;
    showColumns: string[];
  };
  transaction: {
    merchants: number[];
    reference: string;
    transaction_type: ("sale" | "refund" | "cancel" | "capture" | "wallet" | "3ds")[];
    transaction_status: ("complete" | "pending" | "failed" | "cancelled" | "refunded" | "declined")[];
    date_type: string;
    date_from: string;
    date_to: string;
    page: number;
    limit: number;
    partnerId?: number;
    showColumns: string[];
    transaction_channel: ("direct" | "sync" | "terminal" | "virtual_terminal")[];
    dataMerchants?: number[];
  };
  refundRequest: {
    merchants: number[];
    reference: string;
    transaction_type: ("sale" | "refund" | "cancel" | "capture" | "wallet" | "3ds")[];
    transaction_status: ("complete" | "pending" | "failed" | "cancelled" | "refunded" | "declined")[];
    date_type: string;
    date_from: string;
    date_to: string;
    page: number;
    limit: number;
    partnerId?: number;
    showColumns: string[];
    transaction_channel: ("direct" | "sync" | "terminal" | "virtual_terminal")[];
    dataMerchants?: number[];
  };
  disbursement: {
    partnerId?: number;
    merchants: number[];
    date_to?: string;
    date_from?: string;
    status?: ("not_paid_out" | "paid_out" | "pending")[];
    page?: number;
    limit?: number;
    payout_id?: string;
    showColumns: string[];
    dataMerchants?: number[];
  };
  newPayout: {
    partnerId?: number;
    merchants: number[];
    date_to?: string;
    date_from?: string;
    status?: ("not_paid_out" | "paid_out" | "pending")[];
    page?: number;
    limit?: number;
    payout_id?: string;
    showColumns: string[];
    dataMerchants?: number[];
  };
  chargeback: {
    partnerId?: number;
    merchants?: number[];
    date_to?: string;
    date_from?: string;
    status?: ChargeBackStatus[];
    page?: number;
    limit?: number;
    date_type?: ChargebackDateType;
    transaction_id?: number;
    charge_id?: number;
    sortBy?: ChargebackSortBy;
    showColumns: string[];
    dataMerchants?: number[];
  };
  chargebackManagement: {
    partnerId?: number;
    merchants?: number[];
    date_to?: string;
    date_from?: string;
    status?: ChargeBackStatus[];
    page?: number;
    limit?: number;
    date_type?: ChargebackDateType;
    transaction_id?: number;
    charge_id?: number;
    sortBy?: ChargebackSortBy;
    showColumns: string[];
    dataMerchants?: number[];
  };
  chargeAutomation: {
    partnerId?: number;
    merchants?: number[];
    date_from?: string;
    date_to?: string;
    status?: ("processed" | "cancelled" | "waiting" | "failed")[];
    currency?: string[];
    page?: number;
    limit?: number;
    dataMerchants?: number[];
  };
  payoutManagement: {
    partnerId?: number;
    merchants: number[];
    date_to?: string;
    date_from?: string;
    status?: "not_paid_out" | "paid_out" | "pending"[];
    page?: number;
    limit?: number;
    payout_id?: string;
    showColumns: string[];
    dataMerchants?: number[];
  };
  userManagement: {
    page: number;
    limit: number;
    searchText: string;
    userTypes: any;
    status: any;
  };
  partnerManagement: {
    limit: number;
    page: number;
    search: string;
  };
  merchantManagement: {
    limit: number;
    page: number;
    search: string;
    type: string;
  };
  customerManagement: {
    limit: number;
    page: number;
    search: string;
  };
  invoiceManagement: {
    limit: number;
    page: number;
    search: string;
  };
  auditLog: {
    page: number;
    limit: number;
    searchText: string;
    fromDate: any;
    toDate: any;
  };
  activityLog: {
    page: number;
    limit: number;
    merchants: number[];
    fromDate: any;
    toDate: any;
  };
  webhook: {
    partnerId: number,
    page: number;
    limit: number;
  };
  onboarding: {
    partners: number[];
    from_date: any;
    to_date: any;
    search: string;
    case_status: string[];
    status: string[];
    page: number;
    limit: number;
  };
  onboardingManagement: {
    partners: number[];
    from_date: any;
    to_date: any;
    search: string;
    case_status: string[];
    status: string[];
    page: number;
    limit: number;
  };
  emailRecipient: {
    partnerId: number,
    page: number;
    limit: number;
    search: string;
  };
  pspManagement: {
    page: number;
    limit: number;
    search: string;
  };
  chargebackFee: {
    page: number;
    limit: number;
    search: string;
  };
  vaultService: {
    partnerId?: number;
    merchants: number[];
    date_to?: any;
    date_from?: any;
    previous_date_from?: any;
    previous_date_to?: any;
    status?: ("not_paid_out" | "paid_out" | "pending")[];
    page?: number;
    limit?: number;
    payout_id?: string;
    showColumns?: string[];
    dataMerchants?: number[];
  };
  receivable: {
    partnerId?: number;
    merchants: number[];
    date_to?: string;
    date_from?: string;
    status?: "not_paid_out" | "paid_out" | "pending"[];
    page?: number;
    limit?: number;
    payout_id?: string;
    showColumns: string[];
    dataMerchants?: number[];
  };
  adjustment: {
    partnerId?: number;
    merchants: number[];
    date_to?: string;
    date_from?: string;
    status?: "pending" | "cancelled" | "approved" | "rejected"[];
    page?: number;
    limit?: number;
    date_type?: string;
    adjustment_type?: string;
    search?: string;
    dataMerchants?: number[];
  };
  downloadFile: {
    fileName: string,
    typeFile: string,
    status: string,
    fileDownloadId: number,
    currentFileName: string,
    chargeMode?: boolean,
    // Actual filename that was downloaded (from backend Content-Disposition / S3 path); notification prefers this over the FE-built name.
    exportedFileName?: string,
  };
  vaultIntegration: {
    partnerId?: number;
    page?: number;
    limit?: number;
  };
  paymentInfo: {
    merchants: number[];
    reference: string;
    status: "waiting" | "expired" | "completed"[];
    date_type: string;
    date_from: string;
    date_to: string;
    page: number;
    limit: number;
    partnerId?: number;
    showColumns: string[];
    dataMerchants?: number[];
  };
  userManagementSearch: {
    partners: number[];
    userTypes: any;
    page: number;
    limit: number;
    status: any;
    searchText: string;
  },
  refundRequestManagement: {
    partnerIds?: number[];
    merchants: number[];
    date_to?: any;
    date_from?: any;
    previous_date_from?: any;
    previous_date_to?: any;
    page?: number;
    limit?: number;
    search?: any;
    dataMerchants?: number[];
    showColumns: string[];
    order: string,
    sortBy: string;
  };
  transferFund: {
    payer?: string[];
    date_to?: any;
    date_from?: any;
    page?: number;
    limit?: number;
    search?: any;
    status?: string[];
    order: string,
    sortBy: string;
  };
}

const currentDate = dayjs();
const to_date = moment();
const from_date = moment().subtract(1, "month");

const rangeInDays = to_date.diff(from_date, "days");

const previous_to_date = moment(from_date).subtract(1, "day");
const previous_from_date = moment(previous_to_date).subtract(rangeInDays, "days");

const initialState: SessionUserProps = {
  dashboard: {
    from_date: from_date.format("YYYY-MM-DD"),
    to_date: to_date.format("YYYY-MM-DD"),
    previous_from_date: previous_from_date.format("YYYY-MM-DD"),
    previous_to_date: previous_to_date.format("YYYY-MM-DD"),
    showColumns: [
      "ref_id",
      "Amount",
      "Currency",
      "card_holder",
      "guest_name",
      "transaction_type",
      "transaction_status",
      "created_at",
      "payout_date",
      "transaction_channel"
    ],
  },
  transaction: {
    merchants: [],
    partnerId: undefined,
    reference: "",
    transaction_type: [],
    transaction_status: [],
    transaction_channel: [],
    date_type: "created_at",
    date_from: moment().subtract(1, "month").format("YYYY-MM-DD"),
    date_to: moment().format("YYYY-MM-DD"),
    page: 1,
    limit: 10,
    showColumns: [
      "ref_id",
      "Amount",
      "Currency",
      "card_holder",
      "guest_name",
      "transaction_type",
      "transaction_status",
      "created_at",
      "payout_date",
      "transaction_channel",
      "merchant_name"
    ],
  },
  refundRequest: {
    merchants: [],
    partnerId: undefined,
    reference: "",
    transaction_type: [],
    transaction_status: [],
    transaction_channel: [],
    date_type: "created_at",
    date_from: moment().subtract(1, "month").format("YYYY-MM-DD"),
    date_to: moment().format("YYYY-MM-DD"),
    page: 1,
    limit: 10,
    showColumns: [
      "ref_id",
      "Amount",
      "Currency",
      "card_holder",
      "guest_name",
      "transaction_type",
      "transaction_status",
      "created_at",
      "payout_date",
      "transaction_channel",
      "merchant_name"
    ],
  },
  disbursement: {
    partnerId: undefined,
    merchants: [],
    date_from: moment().subtract(1, "month").format("YYYY-MM-DD"),
    date_to: moment().format("YYYY-MM-DD"),
    status: [],
    page: 1,
    limit: 10,
    payout_id: "",
    showColumns: [
      "payout_date_humanize",
      "merchant_name",
      "charge_amount",
      "fee",
      "tax",
      "settlement_amount",
      "payable_amount",
      "paid_amount",
      "payout_type",
      "psp",
      "status"
    ],
  },
  newPayout: {
    partnerId: undefined,
    merchants: [],
    date_from: moment().subtract(1, "month").format("YYYY-MM-DD"),
    date_to: moment().format("YYYY-MM-DD"),
    status: [],
    page: 1,
    limit: 10,
    payout_id: "",
    showColumns: [
      "payout_date_humanize",
      "merchant_name",
      "charge_amount",
      "fee",
      "tax",
      "settlement_amount",
      "payable_amount",
      "paid_amount",
      "payout_type",
      "psp",
      "status"
    ],
  },
  chargeback: {
    partnerId: undefined,
    merchants: [],
    date_from: moment().subtract(1, "month").format("YYYY-MM-DD"),
    date_to: moment().format("YYYY-MM-DD"),
    status: [],
    page: 1,
    limit: 10,
    showColumns: [
      "chargeback_id",
      "chargeback_amount",
      "updated_at",
      "by_user",
      "notify_date",
      "action_due_date",
      "status",
      "merchant_name",
      "mode_3ds"
    ],
  },
  chargebackManagement: {
    partnerId: undefined,
    merchants: [],
    date_from: moment().subtract(1, "month").format("YYYY-MM-DD"),
    date_to: moment().format("YYYY-MM-DD"),
    status: [],
    page: 1,
    limit: 10,
    showColumns: [
      "chargeback_id",
      "chargeback_amount",
      "updated_at",
      "by_user",
      "notify_date",
      "action_due_date",
      "status",
      "merchant_name",
      "mode_3ds"
    ],
  },
  chargeAutomation: {
    merchants: [],
    date_from: moment().subtract(1, "month").format("YYYY-MM-DD"),
    date_to: moment().format("YYYY-MM-DD"),
    status: [],
    currency: [],
    page: 1,
    limit: 10,
  },
  payoutManagement: {
    partnerId: undefined,
    merchants: [],
    date_from: moment().subtract(1, "month").format("YYYY-MM-DD"),
    date_to: moment().format("YYYY-MM-DD"),
    status: [],
    page: 1,
    limit: 10,
    payout_id: "",
    showColumns: [
      "payout_date_humanize",
      "merchant_name",
      "charge_amount",
      "fee",
      "tax",
      "settlement_amount",
      "payable_amount",
      "paid_amount",
      "payout_type",
      "psp",
      "status",
      "processed_date_humanize"
    ],
  },
  userManagement: {
    page: 1,
    limit: 10,
    searchText: "",
    userTypes: [],
    status: [],
  },
  partnerManagement: {
    limit: 10,
    page: 1,
    search: "",
  },
  merchantManagement: {
    limit: 10,
    page: 1,
    search: "",
    type: "",
  },
  customerManagement: {
    limit: 10,
    page: 1,
    search: "",
  },
  invoiceManagement: {
    limit: 10,
    page: 1,
    search: "",
  },
  auditLog: {
    page: 1,
    limit: 10,
    searchText: "",
    fromDate: "",
    toDate: "",
  },
  activityLog: {
    page: 1,
    limit: 10,
    merchants: [],
    fromDate: moment().subtract(1, "month").format("YYYY-MM-DD"),
    toDate: moment().format("YYYY-MM-DD"),
  },
  webhook: {
    partnerId: 0,
    page: 1,
    limit: 10,
  },
  onboarding: {
    from_date: moment().subtract(1, "month").format("YYYY-MM-DD"),
    to_date: moment().format("YYYY-MM-DD"),
    limit: 10,
    page: 1,
    partners: [],
    search: "",
    case_status: [],
    status: [],
  },
  onboardingManagement: {
    from_date: moment().subtract(1, "month").format("YYYY-MM-DD"),
    to_date: moment().format("YYYY-MM-DD"),
    limit: 10,
    page: 1,
    partners: [],
    search: "",
    case_status: [],
    status: [],
  },
  emailRecipient: {
    partnerId: 0,
    page: 1,
    limit: 10,
    search: "",
  },
  pspManagement: {
    page: 1,
    limit: 10,
    search: "",
  },
  chargebackFee: {
    page: 1,
    limit: 10,
    search: "",
  },
  receivable: {
    partnerId: undefined,
    merchants: [],
    date_from: moment().subtract(1, "month").format("YYYY-MM-DD"),
    date_to: moment().format("YYYY-MM-DD"),
    status: [],
    page: 1,
    limit: 10,
    payout_id: "",
    showColumns: [
      "payout_date_humanize",
      "merchant_name",
      "charge_amount",
      "fee",
      "tax",
      "settlement_amount",
      "payout_amount",
      "fx_rate",
      "payable_amount",
      "paid_amount",
      "payout_type",
      "psp",
      "status"
    ],
  },
  adjustment: {
    partnerId: undefined,
    merchants: [],
    date_from: moment().subtract(1, "month").format("YYYY-MM-DD"),
    date_to: moment().format("YYYY-MM-DD"),
    status: [],
    page: 1,
    limit: 10,
    date_type: "",
    search: ""
  },
  vaultService: {
    partnerId: undefined,
    merchants: [],
    date_from: moment().subtract(1, "month").format("YYYY-MM-DD"),
    date_to: moment().format("YYYY-MM-DD"),
    status: [],
    page: 1,
    limit: 10,
    payout_id: "",
    showColumns: [],
    previous_date_from: moment(currentDate.subtract(1, "month").format("YYYY-MM-DD"))
      .subtract(
        moment(currentDate.format("YYYY-MM-DD")).diff(
          moment(currentDate.subtract(1, "month").format("YYYY-MM-DD")),
          "days"
        ),
        "days"
      )
      .format("YYYY-MM-DD"),
    previous_date_to: moment(currentDate.subtract(1, "month").format("YYYY-MM-DD"))
      .subtract(1, "days")
      .format("YYYY-MM-DD"),
  },
  downloadFile: {
    fileName: '',
    typeFile: '',
    status: '',
    fileDownloadId: 0,
    currentFileName: '',
    chargeMode: false,
    exportedFileName: '',
  },
  vaultIntegration: {
    partnerId: undefined,
    page: 1,
    limit: 10,
  },
  paymentInfo: {
    merchants: [],
    partnerId: undefined,
    reference: "",
    status: [],
    date_type: "created_at",
    date_from: moment().subtract(1, "month").format("YYYY-MM-DD"),
    date_to: moment().format("YYYY-MM-DD"),
    page: 1,
    limit: 10,
    showColumns: [
      "alias",
      "booking_ref",
      "booking_info",
      "booking_info",
      "amount",
      "status",
      "created_at",
      "expired_date"
    ],
  },
  userManagementSearch:{
    partners: [],
    userTypes: [],
    page: 1,
    limit: 10,
    status: [],
    searchText: "",
  },
  refundRequestManagement: {
    partnerIds: [],
    merchants: [],
    date_from: moment().subtract(1, "month").format("YYYY-MM-DD"),
    date_to: moment().format("YYYY-MM-DD"),
    page: 1,
    limit: 10,
    previous_date_from: moment(currentDate.subtract(1, "month").format("YYYY-MM-DD"))
      .subtract(
        moment(currentDate.format("YYYY-MM-DD")).diff(
          moment(currentDate.subtract(1, "month").format("YYYY-MM-DD")),
          "days"
        ),
        "days"
      )
      .format("YYYY-MM-DD"),
    previous_date_to: moment(currentDate.subtract(1, "month").format("YYYY-MM-DD"))
      .subtract(1, "days")
      .format("YYYY-MM-DD"),
    search: '',
    showColumns: [
      "ref_id",
      "Amount",
      "Currency",
      "card_holder",
      "guest_name",
      "created_at",
      "transaction_channel",
      "merchant_name"
    ],
    order: "desc",
    sortBy: "created_at"
  },
  transferFund: {
    payer: [],
    date_from: moment().subtract(1, "month").format("YYYY-MM-DD"),
    date_to: moment().format("YYYY-MM-DD"),
    status: [],
    page: 1,
    limit: 10,
    search: '',
    order: "desc",
    sortBy: "created_at"
  },
};

const sessionUserSlice = createSlice({
  name: "sessionUserSlice",
  initialState,
  reducers: {
    updateSessionUser: (state, action: PayloadAction<SessionUserProps>) => {
      const newState = {
        ...action.payload,
      };
      sessionStorage.setItem(SESSION_USER, JSON.stringify({ ...state, ...newState }));
      return (state = { ...state, ...newState });
    },
    updateSesstionUserFromSessionStorage: (state) => {
      const session = sessionStorage.getItem(SESSION_USER);
      if (session) {
        return (state = JSON.parse(session));
      } else {
        sessionStorage.setItem(SESSION_USER, JSON.stringify({ ...state }));
      }
    },
  },
});

export default sessionUserSlice.reducer;

export const { updateSessionUser, updateSesstionUserFromSessionStorage } = sessionUserSlice.actions;
